import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import { IconPlus } from './Icons';
import { useAuth } from '../contexts/AuthContext';
import { logActivity } from '../lib/activityLog';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getDefaultStampAndSignature } from '../lib/companyAssets';
import { sendEmail } from '../services/emailService';
import './Invoices.css';
import headImg from '../head.png';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
const noOpLock = async (_name, _acquireTimeout, fn) => fn();
const invoicesSupabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true, lock: noOpLock },
});

const FETCH_TIMEOUT_MS = 12000;

const logoUrl = process.env.PUBLIC_URL + '/logo.png';
const currentYear = new Date().getFullYear();
const CURRENCY = 'OMR';
const COMPANY_NAME = 'EAST COMPASS Digital Technology LLC';
const COMPANY_NAME_MAIN = 'EAST COMPASS';
const COMPANY_NAME_SUB = 'Digital Technology LLC';
const COMPANY_NAME_AR = 'بوصلة الشرق للتكنولوجيا الرقمية ش.م.م';
const COMPANY_PHONE = '+968 9888 3137';
const COMPANY_ADDRESS = 'P.O.Box 121, Building No: 7351, Flat No: 11 & 12, Mabelah Muscat, Sultanate of Oman';
const COMPANY_EMAIL = 'ecdtinfo@ecdtooman.com';
const COMPANY_WEB = 'www.ecdtoman.com';

// PDF layout: same headers, footers and margins as Procurement
const PDF_MARGIN_MM = 10;
const PDF_HEADER_HEIGHT_MM = 28;
const PDF_FOOTER_RESERVE_MM = 24;
const PDF_FOOTER_TEXT_Y_OFFSET_MM = 18;
const PDF_FOOTER_BAR_HEIGHT_MM = 6;

function drawPdfHeaderFooter(pdf, pageW, pageH, headerDataUrl) {
  if (headerDataUrl) {
    pdf.addImage(headerDataUrl, 'PNG', 0, 0, pageW, PDF_HEADER_HEIGHT_MM);
  }
  const footerY = pageH - PDF_FOOTER_TEXT_Y_OFFSET_MM;
  pdf.setFontSize(8);
  pdf.setTextColor(15, 23, 42);
  pdf.text(`${COMPANY_PHONE}  |  ${COMPANY_ADDRESS}  |  ${COMPANY_EMAIL}  |  ${COMPANY_WEB}`, pageW / 2, footerY, { align: 'center' });
  pdf.setFillColor(230, 126, 34);
  pdf.rect(0, pageH - PDF_FOOTER_BAR_HEIGHT_MM, pageW, PDF_FOOTER_BAR_HEIGHT_MM, 'F');
}

async function loadImageDataUrl(src) {
  if (!src) return null;
  try {
    const resp = await fetch(src);
    const blob = await resp.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

const MARGIN_PERCENT_OPTIONS = Array.from({ length: 21 }, (_, i) => i * 5); // 0, 5, 10, ... 100
const VAT_DEFAULT = 5;

const emptyLine = () => ({ description: '', tender_description: '', quantity: 1, uom: 'Pcs', unit_price: 0, amount: 0 });

// Simple OMR amount in words (1 OMR = 1000 baisa)
function amountInWordsOMR(num) {
  const n = Number(num);
  if (isNaN(n) || n < 0) return '';
  const rial = Math.floor(n);
  const baisa = Math.round((n - rial) * 1000);
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
  const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  function toWordsHundreds(x) {
    if (x === 0) return '';
    if (x < 10) return ones[x];
    if (x < 20) return teens[x - 10];
    if (x < 100) return (tens[Math.floor(x / 10)] + ' ' + ones[x % 10]).trim();
    return (ones[Math.floor(x / 100)] + ' Hundred' + (x % 100 ? ' ' + toWordsHundreds(x % 100) : '')).trim();
  }
  function toWords(x) {
    if (x === 0) return 'Zero';
    if (x < 1000) return toWordsHundreds(x);
    if (x < 1000000) return (toWordsHundreds(Math.floor(x / 1000)) + ' Thousand' + (x % 1000 ? ', ' + toWordsHundreds(x % 1000) : '')).trim();
    return (toWordsHundreds(Math.floor(x / 1000000)) + ' Million' + (x % 1000000 ? ' ' + toWords(Math.floor(x / 1000)) + ' Thousand' + (x % 1000 ? ', ' + toWordsHundreds(x % 1000) : '') : '')).trim();
  }
  const rialStr = toWords(rial);
  const baisaStr = baisa ? toWords(baisa) : 'Zero';
  return `Omani Rial – ${rialStr} and ${baisaStr} Baisa Only.`;
}

export default function Invoices() {
  const { user } = useAuth();
  const [view, setView] = useState('list');
  const [docType, setDocType] = useState('invoice'); // 'invoice' | 'rfq' – same bill, RFQ has different title and no receiver signature
  const [clients, setClients] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [items, setItems] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pdfExporting, setPdfExporting] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const templateRef = useRef(null);

  const [form, setForm] = useState({
    clientId: '',
    departmentId: '',
    billDate: new Date().toISOString().slice(0, 10),
    dueDate: '',
    deliveryDate: '',
    receivedBy: '',
    receiverSignDate: '',
    refNo: '',
    tenderNo: '',
    orderNo: '',
    project: '',
    subject: '',
    note: '',
    marginPercent: String(VAT_DEFAULT),
  });
  const [lineItems, setLineItems] = useState([emptyLine()]);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [listFilter, setListFilter] = useState('all'); // 'all' | 'invoice' | 'rfq'

  const runWakeUp = useCallback(() => {
    return Promise.race([
      invoicesSupabase.from('profiles').select('id').limit(1),
      new Promise((_, reject) => setTimeout(() => reject(new Error('wakeup')), 5000)),
    ]).catch(() => {});
  }, []);

  const fetchClients = useCallback(async () => {
    const { data, error } = await invoicesSupabase.from('clients').select('id, name, email').order('name', { ascending: true, nullsFirst: false });
    if (!error) setClients(data ?? []);
  }, []);
  const fetchDepartments = useCallback(async () => {
    const { data, error } = await invoicesSupabase.from('departments').select('id, code, name').order('name', { ascending: true, nullsFirst: false });
    if (!error) setDepartments(data ?? []);
  }, []);
  const fetchItems = useCallback(async () => {
    const { data, error } = await invoicesSupabase.from('items').select('id, title, rate').order('title', { ascending: true, nullsFirst: false });
    if (!error) setItems(data ?? []);
  }, []);
  const fetchInvoices = useCallback(async () => {
    const { data, error } = await invoicesSupabase
      .from('invoices')
      .select('id, invoice_id, bill_date, total_invoiced, payment_received, due, status, client_id, doc_type, clients(name, email)')
      .order('created_at', { ascending: false });
    if (!error) setInvoices(data ?? []);
  }, []);

  const location = useLocation();

  const refreshSessionAndFetch = useCallback(async () => {
    setLoading(true);
    try {
      await invoicesSupabase.auth.getSession();
      await runWakeUp();
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out. Try refreshing.')), FETCH_TIMEOUT_MS)
      );
      await Promise.race([
        Promise.all([fetchClients(), fetchDepartments(), fetchItems(), fetchInvoices()]),
        timeoutPromise,
      ]);
    } finally {
      setLoading(false);
    }
  }, [runWakeUp, fetchClients, fetchDepartments, fetchItems, fetchInvoices]);

  useEffect(() => {
    if (location.pathname !== '/sales/invoices') return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { error: sessionErr } = await invoicesSupabase.auth.getSession();
      if (cancelled) return;
      if (sessionErr) {
        setLoading(false);
        return;
      }
      await runWakeUp();
      if (cancelled) return;
      try {
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Request timed out. Try refreshing.')), FETCH_TIMEOUT_MS)
        );
        await Promise.race([
          Promise.all([fetchClients(), fetchDepartments(), fetchItems(), fetchInvoices()]),
          timeoutPromise,
        ]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [location.pathname, runWakeUp, fetchClients, fetchDepartments, fetchItems, fetchInvoices]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      refreshSessionAndFetch();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refreshSessionAndFetch]);

  const getNextDocNumber = useCallback((departmentCode, type = 'invoice') => {
    if (!departmentCode) return 1;
    const prefix = type === 'rfq' ? `RFQ-${currentYear}-${departmentCode}-` : `${currentYear}-${departmentCode}-`;
    const existing = invoices.filter(
      (inv) => (inv.doc_type || 'invoice') === type && inv.invoice_id && inv.invoice_id.startsWith(prefix)
    );
    const numbers = existing.map((inv) => {
      const num = parseInt(inv.invoice_id.slice(prefix.length), 10);
      return isNaN(num) ? 0 : num;
    });
    const max = numbers.length ? Math.max(...numbers) : 0;
    return max + 1;
  }, [invoices]);

  const selectedDepartment = departments.find((d) => d.id === form.departmentId);
  const departmentCode = selectedDepartment?.code || 'XXX';
  const nextNum = form.departmentId ? getNextDocNumber(departmentCode, docType) : 0;
  const defaultNumber = docType === 'rfq' ? `RFQ-${currentYear}-${departmentCode}-${String(nextNum).padStart(3, '0')}` : `${currentYear}-${departmentCode}-${String(nextNum).padStart(3, '0')}`;
  const displayInvoiceNumber = invoiceNumber || (form.departmentId ? defaultNumber : '—');
  const clientName = (clients.find((c) => c.id === form.clientId)?.name || '').trim() || '—';
  const departmentDisplay = (selectedDepartment?.code || selectedDepartment?.name || '').trim() || '—';

  const filteredInvoices = React.useMemo(() => {
    if (listFilter === 'all') return invoices;
    return invoices.filter((inv) => (inv.doc_type || 'invoice') === listFilter);
  }, [invoices, listFilter]);

  const insights = React.useMemo(() => {
    const rfqCount = invoices.filter((inv) => (inv.doc_type || 'invoice') === 'rfq').length;
    return {
      totalInvoiced: filteredInvoices.reduce((s, i) => s + (Number(i.total_invoiced) || 0), 0),
      count: filteredInvoices.length,
      rfqCount,
    };
  }, [filteredInvoices, invoices]);

  const [markingSentId, setMarkingSentId] = useState(null);
  const escapeHtml = (value) =>
    String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const sendDocEmail = useCallback(async ({ to, invoiceNo, type, billDate, client, totalAmount, subjectText }) => {
    const docLabel = type === 'rfq' ? 'RFQ' : 'Invoice';
    const subject = `${docLabel} ${invoiceNo || ''} - ${subjectText || client || 'ECDT Portal'}`.trim();
    const body = [
      `Dear ${client || 'Client'},`,
      '',
      `Please find your ${docLabel} details below:`,
      `Number: ${invoiceNo || '—'}`,
      `Date: ${billDate || '—'}`,
      `Subject/Project: ${subjectText || '—'}`,
      `Total (${CURRENCY}): ${Number(totalAmount || 0).toFixed(2)}`,
      '',
      'Thank you,',
      COMPANY_NAME,
    ].join('\n');
    const html = `<div style="font-family:system-ui,-apple-system,sans-serif;color:#0f172a;max-width:640px">
      <p>Dear ${escapeHtml(client || 'Client')},</p>
      <p>Please find your <strong>${escapeHtml(docLabel)}</strong> details below:</p>
      <table style="border-collapse:collapse;font-size:14px">
        <tr><td style="padding:6px 14px 6px 0;color:#64748b">Number</td><td><strong>${escapeHtml(invoiceNo || '—')}</strong></td></tr>
        <tr><td style="padding:6px 14px 6px 0;color:#64748b">Date</td><td>${escapeHtml(billDate || '—')}</td></tr>
        <tr><td style="padding:6px 14px 6px 0;color:#64748b">Subject/Project</td><td>${escapeHtml(subjectText || '—')}</td></tr>
        <tr><td style="padding:6px 14px 6px 0;color:#64748b">Total</td><td>${escapeHtml(CURRENCY)} ${escapeHtml(Number(totalAmount || 0).toFixed(2))}</td></tr>
      </table>
      <p style="margin-top:16px">Thank you,<br/>${escapeHtml(COMPANY_NAME)}</p>
    </div>`;
    return sendEmail({ to, subject, body, html });
  }, []);

  const markAsSent = useCallback(async (invoiceId) => {
    setMarkingSentId(invoiceId);
    try {
      await runWakeUp();
      const inv = invoices.find((i) => i.id === invoiceId);
      const clientEmail = (inv?.clients?.email || '').trim();
      if (!clientEmail) throw new Error('Client email is missing. Add an email to the client first.');
      const sent = await sendDocEmail({
        to: clientEmail,
        invoiceNo: inv?.invoice_id,
        type: inv?.doc_type || 'invoice',
        billDate: inv?.bill_date,
        client: inv?.clients?.name,
        totalAmount: inv?.total_invoiced,
        subjectText: inv?.invoice_id,
      });
      if (!sent.success) throw new Error(sent.error || 'Email send failed.');
      const { data, error } = await invoicesSupabase.from('invoices').update({ status: 'sent' }).eq('id', invoiceId).select('invoice_id').single();
      if (error) throw new Error(error.message);
      await fetchInvoices();
      await logActivity(invoicesSupabase, user?.id, {
        action: 'updated',
        entity_type: 'invoices',
        entity_id: String(invoiceId),
        title: data?.invoice_id || 'Invoice (marked sent)',
      });
      setSaveStatus('Email sent and marked as sent.');
    } catch (e) {
      setSaveStatus(e?.message || 'Failed to send email.');
    } finally {
      setMarkingSentId(null);
    }
  }, [runWakeUp, fetchInvoices, user?.id, invoices, sendDocEmail]);

  useEffect(() => {
    if (view === 'editor' && form.departmentId && !editingId) {
      const code = departments.find((d) => d.id === form.departmentId)?.code;
      if (code) {
        const n = getNextDocNumber(code, docType);
        const num = String(n).padStart(3, '0');
        setInvoiceNumber(docType === 'rfq' ? `RFQ-${currentYear}-${code}-${num}` : `${currentYear}-${code}-${num}`);
      }
    }
  }, [view, form.departmentId, departments, getNextDocNumber, docType, editingId]);

  const openEditor = (invoiceId = null, type = 'invoice') => {
    setEditingId(invoiceId);
    setDocType(type);
    setSaveStatus(null);
    if (!invoiceId) {
      setLoadingInvoice(false);
      setForm({
        clientId: '',
        departmentId: '',
        billDate: new Date().toISOString().slice(0, 10),
        dueDate: '',
        deliveryDate: '',
        receivedBy: '',
        receiverSignDate: '',
        refNo: '',
        tenderNo: '',
        orderNo: '',
        project: '',
        subject: '',
        note: '',
        marginPercent: String(VAT_DEFAULT),
      });
      setLineItems([emptyLine()]);
      setInvoiceNumber('');
      setView('editor');
      return;
    }
    setView('editor');
    setLoadingInvoice(true);
    (async () => {
      try {
        await runWakeUp();
        const { data: inv, error: invErr } = await invoicesSupabase
          .from('invoices')
          .select('*')
          .eq('id', invoiceId)
          .single();
        if (invErr || !inv) {
          setSaveStatus('Invoice not found.');
          return;
        }
        const { data: lines, error: linesErr } = await invoicesSupabase
          .from('invoice_line_items')
          .select('*')
          .eq('invoice_id', invoiceId)
          .order('created_at', { ascending: true });
        const items = (linesErr ? [] : lines ?? []).map((row) => ({
          description: row.description ?? '',
          tender_description: row.tender_description ?? '',
          quantity: Number(row.quantity) ?? 1,
          uom: row.uom ?? 'Pcs',
          unit_price: Number(row.unit_price) ?? 0,
          amount: Number(row.amount) ?? 0,
        }));
        setForm({
          clientId: inv.client_id ?? '',
          departmentId: inv.department_id ?? '',
          billDate: inv.bill_date ? inv.bill_date.slice(0, 10) : new Date().toISOString().slice(0, 10),
          dueDate: inv.due_date ? inv.due_date.slice(0, 10) : '',
          deliveryDate: inv.delivery_date ? inv.delivery_date.slice(0, 10) : '',
          receivedBy: inv.received_by ?? '',
          receiverSignDate: inv.receiver_sign_date ? inv.receiver_sign_date.slice(0, 10) : '',
          refNo: inv.ref_no ?? '',
          tenderNo: inv.tender_no ?? '',
          orderNo: inv.order_no ?? '',
          project: inv.project ?? '',
          subject: inv.subject ?? '',
          note: inv.note ?? '',
          marginPercent: inv.margin_percent != null ? String(inv.margin_percent) : String(VAT_DEFAULT),
        });
        setLineItems(items.length ? items : [emptyLine()]);
        setInvoiceNumber(inv.invoice_id ?? '');
        setDocType(inv.doc_type === 'rfq' ? 'rfq' : 'invoice');
      } finally {
        setLoadingInvoice(false);
      }
    })();
  };

  const updateLine = (index, field, value) => {
    setLineItems((prev) => {
      const line = prev[index];
      const isQty = field === 'quantity';
      const qtyVal = isQty ? Math.max(0, Math.floor(Number(value)) || 0) : value;
      const next = prev.map((line, i) => (i !== index ? line : { ...line, [field]: isQty ? qtyVal : value }));
      const updated = next[index];
      if (field === 'quantity' || field === 'unit_price') {
        const q = Number(updated.quantity) || 0;
        const u = Number(updated.unit_price) || 0;
        next[index] = { ...updated, amount: Math.round(q * u * 100) / 100 };
      }
      return next;
    });
  };

  const addLine = () => setLineItems((prev) => [...prev, emptyLine()]);
  const removeLine = (index) => setLineItems((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));

  const addItemAsLine = (item) => {
    const rate = Number(item.rate) || 0;
    setLineItems((prev) => [...prev, { description: item.title || '', tender_description: '', quantity: 1, uom: 'Pcs', unit_price: rate, amount: rate }]);
  };

  const subtotal = lineItems.reduce((sum, line) => sum + (Number(line.amount) || 0), 0);
  const subtotalRounded = Math.round(subtotal * 100) / 100;
  const marginPct = Math.min(100, Math.max(0, Number(form.marginPercent) || 0));
  const marginAmount = Math.round(subtotalRounded * (marginPct / 100) * 100) / 100;
  const total = Math.round((subtotalRounded + marginAmount) * 100) / 100;

  const saveToDb = useCallback(async () => {
    await runWakeUp();
    const totalVal = total;
    const marginPctVal = Math.min(100, Math.max(0, Number(form.marginPercent) || 0));
    const invPayload = {
      client_id: form.clientId || null,
      department_id: form.departmentId || null,
      bill_date: form.billDate || null,
      due_date: form.dueDate || null,
      delivery_date: form.deliveryDate || null,
      received_by: form.receivedBy || null,
      receiver_sign_date: form.receiverSignDate || null,
      ref_no: form.refNo || null,
      tender_no: form.tenderNo || null,
      order_no: form.orderNo || null,
      project: form.project || form.subject || null,
      subject: form.subject || form.project || null,
      note: form.note || null,
      margin_percent: marginPctVal,
      total_invoiced: totalVal,
      due: totalVal,
      payment_received: 0,
      status: 'draft',
    };
    invPayload.doc_type = docType;
    invPayload.invoice_id = displayInvoiceNumber !== '—' ? displayInvoiceNumber : null;

    if (editingId) {
      const { error: updErr } = await invoicesSupabase.from('invoices').update(invPayload).eq('id', editingId);
      if (updErr) throw new Error(updErr.message);
      await invoicesSupabase.from('invoice_line_items').delete().eq('invoice_id', editingId);
    } else {
      const { data: inserted, error: insErr } = await invoicesSupabase.from('invoices').insert(invPayload).select('id').single();
      if (insErr) throw new Error(insErr.message);
      if (!inserted?.id) throw new Error('Insert failed');
      setEditingId(inserted.id);
      invPayload.id = inserted.id;
    }
    const invoiceId = editingId || invPayload.id;
    const rows = lineItems.filter(
      (line) => (Number(line.quantity) || 0) > 0 || (Number(line.unit_price) || 0) > 0 || (line.description || '').trim() || (line.tender_description || '').trim()
    );
    const linePayload = rows.map((line) => ({
      invoice_id: invoiceId,
      item_id: null,
      description: (line.description || '').trim() || null,
      tender_description: (line.tender_description || '').trim() || null,
      quantity: Number(line.quantity) || 0,
      uom: (line.uom || '').trim() || 'Pcs',
      unit_price: Number(line.unit_price) || 0,
      amount: Number(line.amount) || 0,
    }));
    if (linePayload.length > 0) {
      const { error: lineErr } = await invoicesSupabase.from('invoice_line_items').insert(linePayload);
      if (lineErr) throw new Error(lineErr.message);
    }
    await fetchInvoices();
    const invTitle = invPayload.invoice_id || invoiceId;
    await logActivity(invoicesSupabase, user?.id, {
      action: editingId ? 'updated' : 'created',
      entity_type: 'invoices',
      entity_id: String(invoiceId),
      title: invTitle || 'Invoice',
    });
    return {
      id: invoiceId,
      invoiceId: invPayload.invoice_id,
      docType,
      billDate: invPayload.bill_date,
      clientId: invPayload.client_id,
      subject: invPayload.subject || invPayload.project || '',
      total: totalVal,
    };
  }, [runWakeUp, form, lineItems, total, displayInvoiceNumber, editingId, fetchInvoices, user?.id, docType]);

  const handleExportPdf = async () => {
    setPdfExporting(true);
    try {
      const { stampUrl, signatureUrl } = await getDefaultStampAndSignature();
      const [headerDataUrl, stampDataUrl, signatureDataUrl] = await Promise.all([
        loadImageDataUrl(headImg),
        stampUrl ? loadImageDataUrl(stampUrl) : Promise.resolve(null),
        signatureUrl ? loadImageDataUrl(signatureUrl) : Promise.resolve(null),
      ]);
      const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
      pdf.setProperties({
        title: docType === 'rfq' ? `Request for Quotation ${displayInvoiceNumber}` : `Tax Invoice ${displayInvoiceNumber}`,
        subject: docType === 'rfq' ? 'Request for Quotation' : 'Tax Invoice & Delivery Note',
        creator: COMPANY_NAME,
        author: COMPANY_NAME,
      });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = PDF_MARGIN_MM;

      if (headerDataUrl) {
        pdf.addImage(headerDataUrl, 'PNG', 0, 0, pageW, PDF_HEADER_HEIGHT_MM);
      }

      let y = PDF_HEADER_HEIGHT_MM + 6;
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text(docType === 'rfq' ? 'Request for Quotation' : 'Tax Invoice', pageW / 2, y, { align: 'center' });
      y += 8;

      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      const txt = (s) => (s ?? '').toString();
      pdf.text(`Date: ${txt(form.billDate)}`, pageW - margin, y, { align: 'right' });
      pdf.text(`Invoice NO: ${txt(displayInvoiceNumber)}`, margin, y);
      y += 5;
      pdf.text(`Ref. No: ${txt(form.refNo || '—')}`, margin, y);
      y += 5;
      pdf.text(`To: ${txt(clientName)}`, margin, y);
      y += 5;
      pdf.text(`Sub: ${txt(form.subject || form.project || '—')}`, margin, y);
      y += 5;
      pdf.text(`Tender No: ${txt(form.tenderNo || '—')}  |  Order No: ${txt(form.orderNo || '—')}`, margin, y);
      y += 8;

      pdf.setFontSize(9);
      pdf.text('Find the required items as following:', margin, y);
      y += 6;

      const tableBody = (lineItems.length ? lineItems : [{ tender_description: '', description: '', quantity: '', uom: 'Pcs', unit_price: 0, amount: 0 }]).map((line, idx) => [
        String(lineItems.length ? idx + 1 : 1),
        txt(line.tender_description),
        txt(line.description),
        String(line.quantity ?? ''),
        txt(line.uom),
        (Number(line.unit_price) || 0).toFixed(2),
        (Number(line.amount) || 0).toFixed(2),
      ]);

      autoTable(pdf, {
        head: [['No', 'Tender Item Description', 'Quoted Item Description', 'Quantity', 'UOM', `Unit Price (${CURRENCY})`, `Total Price (${CURRENCY})`]],
        body: tableBody,
        startY: y,
        margin: { left: margin, right: margin, bottom: PDF_FOOTER_RESERVE_MM },
        tableWidth: pageW - margin * 2,
        showHead: 'everyPage',
        headStyles: { fillColor: [15, 23, 42], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 9 },
        bodyStyles: { fontSize: 9 },
        columnStyles: {
          0: { cellWidth: 10 },
          1: { cellWidth: 'auto' },
          2: { cellWidth: 'auto' },
          3: { cellWidth: 18 },
          4: { cellWidth: 16 },
          5: { cellWidth: 24 },
          6: { cellWidth: 24 },
        },
        didDrawPage: () => {
          drawPdfHeaderFooter(pdf, pageW, pageH, headerDataUrl);
        },
      });

      y = pdf.lastAutoTable?.finalY ?? y;
      y += 8;

      autoTable(pdf, {
        body: [
          ['Invoice Sub Total', `${CURRENCY} ${(subtotalRounded ?? 0).toFixed(2)}`],
          [`Vat ${marginPct ?? 0}%`, `${CURRENCY} ${(marginAmount ?? 0).toFixed(2)}`],
          [`Invoice Total Price with VAT (${CURRENCY})`, `${CURRENCY} ${(total ?? 0).toFixed(2)}`],
        ],
        startY: y,
        margin: { left: margin, right: margin, bottom: PDF_FOOTER_RESERVE_MM },
        tableWidth: pageW - margin * 2,
        theme: 'plain',
        bodyStyles: { fontSize: 9 },
        columnStyles: { 0: { cellWidth: 70, fontStyle: 'bold' }, 1: { cellWidth: 'auto' } },
      });
      y = pdf.lastAutoTable?.finalY ?? y;
      y += 5;
      pdf.setFontSize(9);
      pdf.text(`In words: ${amountInWordsOMR(total)}`, margin, y, { maxWidth: pageW - margin * 2 });
      y += 12;

      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text(docType === 'rfq' ? 'Request for Quotation' : 'Delivery Note', margin, y);
      y += 8;
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Delivery Date: ${txt(form.deliveryDate || '—')}  |  Order No: ${txt(form.orderNo || '—')}`, margin, y);
      y += 8;

      const deliveryBody = (lineItems.length ? lineItems : [{ description: '', quantity: '', uom: 'Pcs' }]).map((line, idx) => [
        String(lineItems.length ? idx + 1 : 1),
        txt(line.description),
        String(line.quantity ?? ''),
        txt(line.uom),
      ]);
      autoTable(pdf, {
        head: [['Srl No', 'Description', 'Quantity', 'UOM']],
        body: deliveryBody,
        startY: y,
        margin: { left: margin, right: margin, bottom: PDF_FOOTER_RESERVE_MM },
        tableWidth: pageW - margin * 2,
        showHead: 'everyPage',
        headStyles: { fillColor: [15, 23, 42], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 9 },
        bodyStyles: { fontSize: 9 },
        columnStyles: { 0: { cellWidth: 16 }, 1: { cellWidth: 'auto' }, 2: { cellWidth: 22 }, 3: { cellWidth: 20 } },
        didDrawPage: () => {
          drawPdfHeaderFooter(pdf, pageW, pageH, headerDataUrl);
        },
      });
      y = pdf.lastAutoTable?.finalY ?? y;
      y += 6;
      autoTable(pdf, {
        body: [
          ['Invoice Sub Total', `${CURRENCY} ${(subtotalRounded ?? 0).toFixed(2)}`],
          [`Vat ${marginPct ?? 0}%`, `${CURRENCY} ${(marginAmount ?? 0).toFixed(2)}`],
          [`Invoice Total Price with VAT (${CURRENCY})`, `${CURRENCY} ${(total ?? 0).toFixed(2)}`],
        ],
        startY: y,
        margin: { left: margin, right: margin, bottom: PDF_FOOTER_RESERVE_MM },
        tableWidth: pageW - margin * 2,
        theme: 'plain',
        bodyStyles: { fontSize: 9 },
        columnStyles: { 0: { cellWidth: 70, fontStyle: 'bold' }, 1: { cellWidth: 'auto' } },
      });
      y = pdf.lastAutoTable?.finalY ?? y;
      y += 10;

      if (docType !== 'rfq') {
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Customer Receipt:', margin, y);
        y += 5;
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9);
        pdf.text('I/We received all items in good condition:', margin, y);
        y += 6;
        pdf.text(`Receiver Name: ${txt(form.receivedBy || '—')}`, margin, y);
        y += 5;
        pdf.text('Sign & Stamp: _________________________', margin, y);
        y += 5;
        pdf.text(`Date: ${txt(form.receiverSignDate || '—')}`, margin, y);
      }

      y += 14;
      const boxW = (pageW - margin * 2 - 15) / 2;
      const boxH = 28;
      pdf.setDrawColor(229, 231, 235);
      pdf.setLineWidth(0.3);
      pdf.rect(margin, y, boxW, boxH, 'S');
      pdf.rect(margin + boxW + 15, y, boxW, boxH, 'S');
      pdf.setFontSize(8);
      pdf.setTextColor(107, 114, 128);
      pdf.text('Authorized Signature', margin + boxW / 2, y + 5, { align: 'center' });
      pdf.text('Company Stamp', margin + boxW + 15 + boxW / 2, y + 5, { align: 'center' });
      if (signatureDataUrl) {
        try {
          pdf.addImage(signatureDataUrl, 'PNG', margin + 4, y + 8, Math.min(boxW - 8, 45), Math.min(boxH - 12, 18));
        } catch {
          // ignore
        }
      }
      if (stampDataUrl) {
        try {
          pdf.addImage(stampDataUrl, 'PNG', margin + boxW + 19, y + 8, Math.min(boxW - 8, 45), Math.min(boxH - 12, 18));
        } catch {
          // ignore
        }
      }

      pdf.save(docType === 'rfq' ? `rfq-${displayInvoiceNumber}.pdf` : `invoice-${displayInvoiceNumber}.pdf`);
    } catch (e) {
      setSaveStatus(e?.message || 'PDF export failed.');
    } finally {
      setPdfExporting(false);
    }
  };

  const handleSaveAndExport = async () => {
    setSaving(true);
    setSaveStatus(null);
    try {
      await saveToDb();
      setSaveStatus('Saved.');
      await handleExportPdf();
      setSaveStatus('Saved and exported.');
    } catch (e) {
      setSaveStatus(e?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndEmail = async () => {
    setSaving(true);
    setEmailing(true);
    setSaveStatus(null);
    try {
      const saved = await saveToDb();
      const client = clients.find((c) => c.id === saved.clientId);
      const to = (client?.email || '').trim();
      if (!to) throw new Error('Client email is missing. Add an email to the client first.');
      const sent = await sendDocEmail({
        to,
        invoiceNo: saved.invoiceId || displayInvoiceNumber,
        type: saved.docType || docType,
        billDate: saved.billDate || form.billDate,
        client: client?.name || clientName,
        totalAmount: saved.total ?? total,
        subjectText: saved.subject || form.subject || form.project,
      });
      if (!sent.success) throw new Error(sent.error || 'Email send failed.');
      if (saved.id) {
        await invoicesSupabase.from('invoices').update({ status: 'sent' }).eq('id', saved.id);
      }
      await fetchInvoices();
      setSaveStatus('Saved and emailed.');
    } catch (e) {
      setSaveStatus(e?.message || 'Save/email failed.');
    } finally {
      setEmailing(false);
      setSaving(false);
    }
  };

  const [turningToInvoice, setTurningToInvoice] = useState(false);
  const turnToInvoice = useCallback(async () => {
    if (!editingId || docType !== 'rfq' || !form.departmentId) return;
    const code = departments.find((d) => d.id === form.departmentId)?.code;
    if (!code) return;
    setTurningToInvoice(true);
    setSaveStatus(null);
    try {
      await runWakeUp();
      const nextNum = getNextDocNumber(code, 'invoice');
      const newInvoiceId = `${currentYear}-${code}-${String(nextNum).padStart(3, '0')}`;
      const { error } = await invoicesSupabase
        .from('invoices')
        .update({ doc_type: 'invoice', invoice_id: newInvoiceId })
        .eq('id', editingId);
      if (error) throw error;
      setDocType('invoice');
      setInvoiceNumber(newInvoiceId);
      await fetchInvoices();
      setSaveStatus('Converted to invoice. You can now add receiver signature and export.');
    } catch (e) {
      setSaveStatus(e?.message || 'Failed to convert.');
    } finally {
      setTurningToInvoice(false);
    }
  }, [runWakeUp, editingId, docType, form.departmentId, departments, getNextDocNumber, fetchInvoices]);

  if (view === 'list') {
    return (
      <div className="invoices-page">
        <div className="invoices-card">
          <div className="invoices-header-row">
            <h1 className="invoices-title">Invoices and RFQs</h1>
            {!loading && (
              <div className="invoices-header-actions">
                <button type="button" className="invoices-header-btn" onClick={() => openEditor(null, 'rfq')}>
                  Create RFQ
                </button>
                <button type="button" className="invoices-header-btn invoices-header-btn-primary" onClick={() => openEditor()}>
                  <IconPlus />
                  Create invoice
                </button>
              </div>
            )}
          </div>
          {loading && <p className="invoices-muted">Loading…</p>}
          {!loading && (
            <>
              {/* Insights */}
              <section className="invoices-insights" aria-label="Invoice and RFQ insights">
                <div className="invoices-insights-cards">
                  <div className="invoices-insight-card">
                    <span className="invoices-insight-label">Total invoiced</span>
                    <span className="invoices-insight-value">{CURRENCY} {insights.totalInvoiced.toFixed(2)}</span>
                  </div>
                  <div className="invoices-insight-card">
                    <span className="invoices-insight-label">Invoices</span>
                    <span className="invoices-insight-value">{insights.count}</span>
                  </div>
                  <div className="invoices-insight-card invoices-insight-card-rfq">
                    <span className="invoices-insight-label">RFQs</span>
                    <span className="invoices-insight-value">{insights.rfqCount}</span>
                  </div>
                </div>
              </section>

              {/* Invoice list */}
              <section className="invoices-list-section" aria-label="Invoices and RFQs">
                <div className="invoices-list-header">
                  <h2 className="invoices-list-title">
                    {listFilter === 'all' ? 'All' : listFilter === 'rfq' ? 'RFQs' : 'Invoices'}
                  </h2>
                  <div className="invoices-list-filter" role="tablist" aria-label="Filter by type">
                    {['all', 'invoice', 'rfq'].map((f) => (
                      <button
                        key={f}
                        type="button"
                        role="tab"
                        aria-selected={listFilter === f}
                        className={`invoices-filter-btn${listFilter === f ? ' active' : ''}`}
                        onClick={() => setListFilter(f)}
                      >
                        {f === 'all' ? 'All' : f === 'rfq' ? 'RFQs' : 'Invoices'}
                      </button>
                    ))}
                  </div>
                </div>
                {filteredInvoices.length > 0 ? (
                  <div className="invoices-table-wrap">
                    <table className="invoices-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Type</th>
                          <th>Client</th>
                          <th>Date</th>
                          <th>Total ({CURRENCY})</th>
                          <th>Status</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredInvoices.map((inv) => (
                          <tr key={inv.id}>
                            <td>{inv.invoice_id || '—'}</td>
                            <td><span className="invoices-table-type" data-type={inv.doc_type || 'invoice'}>{inv.doc_type === 'rfq' ? 'RFQ' : 'Invoice'}</span></td>
                            <td>{inv.clients?.name ?? '—'}</td>
                            <td>{inv.bill_date ? inv.bill_date.slice(0, 10) : '—'}</td>
                            <td>{inv.total_invoiced != null ? Number(inv.total_invoiced).toFixed(2) : '—'}</td>
                            <td>
                              <span className="invoices-table-status" data-status={inv.status || 'draft'}>
                                {inv.status === 'sent' ? 'Sent' : 'Draft'}
                              </span>
                              {(inv.status || 'draft') !== 'sent' && (
                                <button
                                  type="button"
                                  className="invoices-header-btn invoices-send-btn"
                                  onClick={() => markAsSent(inv.id)}
                                  disabled={markingSentId === inv.id}
                                  aria-label="Mark as sent"
                                >
                                  {markingSentId === inv.id ? 'Sending…' : 'Email & mark sent'}
                                </button>
                              )}
                            </td>
                            <td>
                              <button type="button" className="invoices-header-btn invoices-edit-btn" onClick={() => openEditor(inv.id)}>
                                Edit
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="invoices-muted">
                    {listFilter === 'all' ? 'No invoices or RFQs yet.' : listFilter === 'rfq' ? 'No RFQs in this filter.' : 'No invoices in this filter.'} Create one to get started.
                  </p>
                )}
              </section>
            </>
          )}
        </div>
      </div>
    );
  }

  if (loadingInvoice) {
    return (
      <div className="invoices-page">
        <div className="invoices-card">
          <p className="invoices-muted">Loading invoice…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="invoices-page">
      <div className="invoices-card">
        <div className="invoices-editor-actions">
          <button type="button" className="invoices-header-btn" onClick={() => setView('list')}>
            Back
          </button>
          {docType === 'rfq' && <span className="invoices-doc-type-badge">Request for Quotation</span>}
          {docType === 'rfq' && editingId && (
            <button type="button" className="invoices-header-btn invoices-turn-to-invoice-btn" onClick={turnToInvoice} disabled={turningToInvoice}>
              {turningToInvoice ? 'Converting…' : 'Turn to Invoice'}
            </button>
          )}
          <div className="invoices-editor-right">
            {saveStatus && <span className="invoices-status">{saveStatus}</span>}
            <button type="button" className="invoices-header-btn invoices-header-btn-primary" onClick={handleSaveAndExport} disabled={saving || pdfExporting}>
              {saving ? 'Saving…' : pdfExporting ? 'Exporting…' : 'Save and Export'}
            </button>
            <button type="button" className="invoices-header-btn" onClick={handleSaveAndEmail} disabled={saving || emailing}>
              {emailing ? 'Emailing…' : 'Save and Email'}
            </button>
          </div>
        </div>

        <div ref={templateRef} className="invoices-template inv-ecdt">
          <div className="invoices-template-editable">
          {/* Bill header image */}
          <header className="inv-header">
            <img src={headImg} alt="" className="inv-header-img" />
          </header>

          {/* Tax Invoice / RFQ: title + date right */}
          <div className="inv-tax-block">
            <h1 className="inv-tax-title">{docType === 'rfq' ? 'Request for Quotation' : 'Tax Invoice'}</h1>
            <div className="inv-tax-date">
              <span className="inv-label">Date:</span>
              <input type="date" className="estimates-form-input inv-input-inline" value={form.billDate} onChange={(e) => setForm((f) => ({ ...f, billDate: e.target.value }))} />
            </div>
            <div className="inv-meta-rows">
              <div className="invoices-hide-on-pdf inv-meta-row">
                <span className="inv-label">Department:</span>
                <select className="estimates-form-input estimates-form-select inv-input-small" value={form.departmentId} onChange={(e) => setForm((f) => ({ ...f, departmentId: e.target.value }))}>
                  <option value="">— Select department —</option>
                  {departments.map((d) => (<option key={d.id} value={d.id}>{d.code || d.name || 'Unnamed'}</option>))}
                </select>
              </div>
              <div className="inv-meta-row">
                <span className="inv-label">Invoice NO:</span>
                <span className="inv-value">{displayInvoiceNumber}</span>
              </div>
              <div className="invoices-hide-on-pdf inv-meta-row">
                <span className="inv-label">Ref. No:</span>
                <input type="text" className="estimates-form-input inv-input-ref" value={form.refNo} onChange={(e) => setForm((f) => ({ ...f, refNo: e.target.value }))} placeholder="e.g. ECDT/PROC/D/ROP/10/25/25" />
              </div>
              <div className="inv-meta-row inv-meta-row-pdf-only">
                <span className="inv-label">Ref. No:</span>
                <span className="inv-value">{form.refNo || '—'}</span>
              </div>
              <div className="inv-meta-row">
                <span className="inv-label">To:</span>
                <div className="inv-to-block">
                  <select className="estimates-form-input estimates-form-select inv-select-to" value={form.clientId} onChange={(e) => setForm((f) => ({ ...f, clientId: e.target.value }))}>
                    <option value="">— Select client —</option>
                    {clients.map((c) => (<option key={c.id} value={c.id}>{c.name || 'Unnamed'}</option>))}
                  </select>
                  <span className="inv-value inv-to-name">{clientName}</span>
                </div>
              </div>
              <div className="inv-meta-row">
                <span className="inv-label">Sub:</span>
                <input type="text" className="estimates-form-input inv-input-subject" value={form.subject || form.project} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value, project: e.target.value }))} placeholder="Invoice for…" />
              </div>
              <div className="inv-meta-row">
                <span className="inv-label">Tender No:</span>
                <input type="text" className="estimates-form-input inv-input-small" value={form.tenderNo} onChange={(e) => setForm((f) => ({ ...f, tenderNo: e.target.value }))} placeholder="—" />
                <span className="inv-label inv-meta-gap">Order No:</span>
                <input type="text" className="estimates-form-input inv-input-small" value={form.orderNo} onChange={(e) => setForm((f) => ({ ...f, orderNo: e.target.value }))} placeholder="—" />
              </div>
            </div>

            <p className="inv-items-intro">Find the required items as following:</p>
            <div className="invoices-template-addline inv-addline">
              <button type="button" className="invoices-header-btn" onClick={addLine}><IconPlus /> Add line</button>
              {items.length > 0 && (
                <select className="estimates-form-select invoices-pick-item" value="" onChange={(e) => { const id = e.target.value; if (id) { addItemAsLine(items.find((i) => i.id === id)); e.target.value = ''; } }}>
                  <option value="">Pick from items…</option>
                  {items.map((i) => (<option key={i.id} value={i.id}>{i.title} ({CURRENCY} {i.rate})</option>))}
                </select>
              )}
            </div>
            <table className="inv-table">
              <thead>
                <tr>
                  <th className="inv-th-no">No</th>
                  <th>Tender Item Description</th>
                  <th>Quoted Item Description</th>
                  <th className="inv-th-num">Quantity</th>
                  <th className="inv-th-uom">UOM</th>
                  <th className="inv-th-num">Unit Price ({CURRENCY})</th>
                  <th className="inv-th-num">Total Price ({CURRENCY})</th>
                  <th className="invoices-th-action" />
                </tr>
              </thead>
              <tbody>
                {lineItems.map((line, index) => (
                  <tr key={index}>
                    <td className="inv-td-no">{index + 1}</td>
                    <td><input type="text" className="estimates-form-input inv-cell" value={line.tender_description} onChange={(e) => updateLine(index, 'tender_description', e.target.value)} placeholder="—" /></td>
                    <td><input type="text" className="estimates-form-input inv-cell" value={line.description} onChange={(e) => updateLine(index, 'description', e.target.value)} placeholder="Description" /></td>
                    <td className="inv-td-num"><input type="number" min="0" step="1" className="estimates-form-input inv-cell-num" value={line.quantity} onChange={(e) => updateLine(index, 'quantity', e.target.value)} /></td>
                    <td className="inv-td-uom"><input type="text" className="estimates-form-input inv-cell-uom" value={line.uom} onChange={(e) => updateLine(index, 'uom', e.target.value)} placeholder="Pcs" /></td>
                    <td className="inv-td-num"><input type="number" min="0" step="0.01" className="estimates-form-input inv-cell-num" value={line.unit_price} onChange={(e) => updateLine(index, 'unit_price', e.target.value)} /></td>
                    <td className="inv-td-num"><input type="number" min="0" step="0.01" className="estimates-form-input inv-cell-num" value={line.amount} onChange={(e) => updateLine(index, 'amount', e.target.value)} /></td>
                    <td className="invoices-td-action"><button type="button" className="invoices-row-remove" onClick={() => removeLine(index)} aria-label="Remove">×</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="inv-totals">
              <div className="inv-total-row">
                <span className="inv-total-label">Invoice Sub Total</span>
                <span className="inv-total-value">{CURRENCY} {subtotalRounded.toFixed(2)}</span>
              </div>
              <div className="inv-total-row invoices-hide-on-pdf">
                <span className="inv-total-label">Vat <select className="invoices-margin-select inv-vat-select" value={form.marginPercent === '' ? VAT_DEFAULT : form.marginPercent} onChange={(e) => setForm((f) => ({ ...f, marginPercent: e.target.value }))} aria-label="VAT %"><option value="0">0%</option>{MARGIN_PERCENT_OPTIONS.filter((p) => p > 0).map((p) => (<option key={p} value={p}>{p}%</option>))}</select></span>
                <span className="inv-total-value">{CURRENCY} {marginAmount.toFixed(2)}</span>
              </div>
              <div className="inv-total-row inv-total-row-pdf-only"><span className="inv-total-label">Vat {marginPct}%</span><span className="inv-total-value">{CURRENCY} {marginAmount.toFixed(2)}</span></div>
              <div className="inv-total-row">
                <span className="inv-total-label">Invoice Total Price with VAT ({CURRENCY})</span>
                <span className="inv-total-value">{CURRENCY} {total.toFixed(2)}</span>
              </div>
            </div>
            <p className="inv-words">In words: {amountInWordsOMR(total)}</p>
          </div>

          {/* Delivery Note (or RFQ details – same block; RFQ has no receipt/signature below) */}
          <div className="inv-delivery-block">
            <h2 className="inv-delivery-title">{docType === 'rfq' ? 'Request for Quotation' : 'Delivery Note'}</h2>
            <div className="inv-delivery-meta">
              <div className="inv-meta-row">
                <span className="inv-label">Delivery Date:</span>
                <input type="date" className="estimates-form-input inv-input-inline inv-delivery-date" value={form.deliveryDate} onChange={(e) => setForm((f) => ({ ...f, deliveryDate: e.target.value }))} />
              </div>
              <div className="inv-meta-row">
                <span className="inv-label">Order No:</span>
                <input type="text" className="estimates-form-input inv-input-small" value={form.orderNo} onChange={(e) => setForm((f) => ({ ...f, orderNo: e.target.value }))} placeholder="—" />
              </div>
            </div>
            <div className="inv-addline inv-addline-delivery">
              <button type="button" className="invoices-header-btn" onClick={addLine}>
                <IconPlus />
                Add line
              </button>
            </div>
            <table className="inv-table inv-table-delivery">
              <thead>
                <tr>
                  <th className="inv-th-no">Srl No</th>
                  <th>Description</th>
                  <th className="inv-th-num">Quantity</th>
                  <th className="inv-th-uom">UOM</th>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((line, index) => (
                  <tr key={index}>
                    <td className="inv-td-no">{index + 1}</td>
                    <td><input type="text" className="estimates-form-input inv-cell" value={line.description} onChange={(e) => updateLine(index, 'description', e.target.value)} placeholder="—" /></td>
                    <td className="inv-td-num"><input type="number" min="0" step="1" className="estimates-form-input inv-cell-num" value={line.quantity} onChange={(e) => updateLine(index, 'quantity', e.target.value)} /></td>
                    <td className="inv-td-uom"><input type="text" className="estimates-form-input inv-cell-uom" value={line.uom} onChange={(e) => updateLine(index, 'uom', e.target.value)} placeholder="Pcs" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="inv-totals inv-totals-delivery">
              <div className="inv-total-row"><span className="inv-total-label">Invoice Sub Total</span><span className="inv-total-value">{CURRENCY} {subtotalRounded.toFixed(2)}</span></div>
              <div className="inv-total-row"><span className="inv-total-label">Vat {marginPct}%</span><span className="inv-total-value">{CURRENCY} {marginAmount.toFixed(2)}</span></div>
              <div className="inv-total-row"><span className="inv-total-label">Invoice Total Price with VAT ({CURRENCY})</span><span className="inv-total-value">{CURRENCY} {total.toFixed(2)}</span></div>
            </div>
            {docType !== 'rfq' && (
              <div className="inv-receipt">
                <p className="inv-receipt-title">Customer Receipt:</p>
                <p className="inv-receipt-text">I/We received all items in good condition:</p>
                <div className="inv-receipt-box">
                  <div className="inv-receipt-row">
                    <span className="inv-receipt-label">Receiver Name:</span>
                    <span className="inv-receipt-cell"><input type="text" className="estimates-form-input inv-receipt-input" value={form.receivedBy} onChange={(e) => setForm((f) => ({ ...f, receivedBy: e.target.value }))} placeholder="" /></span>
                  </div>
                  <div className="inv-receipt-row inv-receipt-row-sign">
                    <span className="inv-receipt-label">Sign &amp; Stamp:</span>
                    <span className="inv-receipt-cell inv-receipt-cell-sign"></span>
                  </div>
                  <div className="inv-receipt-row">
                    <span className="inv-receipt-label">Date:</span>
                    <span className="inv-receipt-cell inv-receipt-cell-date">
                      <input type="date" className="estimates-form-input inv-receipt-input" value={form.receiverSignDate} onChange={(e) => setForm((f) => ({ ...f, receiverSignDate: e.target.value }))} />
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <footer className="inv-footer">
            <div className="inv-footer-bar" />
            <div className="inv-footer-inner">
              <div className="inv-footer-circle"><span className="inv-footer-icon" aria-hidden>📞</span><span>{COMPANY_PHONE}</span></div>
              <div className="inv-footer-circle"><span className="inv-footer-icon" aria-hidden>📍</span><span>{COMPANY_ADDRESS}</span></div>
              <div className="inv-footer-circle"><span className="inv-footer-icon" aria-hidden>✉</span><span>{COMPANY_EMAIL}<br />{COMPANY_WEB}</span></div>
            </div>
          </footer>
          </div>
        </div>
      </div>
    </div>
  );
}
